import React, { useCallback, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Typography, IconButton, Breadcrumbs, Divider, useTheme } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { SvgIconTableOfContent } from '@modules/ui/icons/EngineReferenceIcons';
import { ClassReferenceParent, ReferenceType } from '@modules/client';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useResponsiveDesign from '@modules/miscellaneous/common/hooks/useResponsiveDesign';
import useElementsShowInView from '@modules/ui/shared/hooks/useElementsShowInView';
import { mainID } from '@modules/layout/layout/constants/layoutConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import OpenSourceEditButton from '@modules/openSource/components/OpenSourceEditButton';
import FeedbackButton from '@modules/feedback/components/FeedbackButton';
import { useShareFeedbackProvider } from '@modules/feedback/context/ShareFeedbackProvider';
import { useRobloxAuthentication } from '@rbx/auth';
import LanguageMenu from '@modules/localization/components/LanguageMenu';
import groupsClient from '@modules/clients/groups';
import { getReferencePageUrl } from '../../utils/apiReferencePageNavUtils';
import {
  labelTranslKeyByReferenceType,
  referenceTypePathMapping,
  summaryID,
  sectionHashToLabelTranslKey,
  ApiReferenceMemberTypeCollectionHash,
} from '../../constants/apiReferenceConstants';
import useReferenceStickyBreadcrumbStyles, {
  getCollapsedIconStyles,
} from './ReferenceStickyBreadcrumb.styles';

export interface ReferenceStickyBreadcrumbProps {
  currentName: string;
  currentType: ReferenceType;
  classReferenceParents: ClassReferenceParent[] | null;
  onTocClick?: () => void;
}

// Helper function to get proper section name using existing utility
const getSectionName = (sectionId: string, translate: (key: string) => string): string | null => {
  // Handle summary-* composite sections (e.g., "summary-properties" -> "properties")
  const normalizedId = sectionId.startsWith(`${summaryID}-`)
    ? sectionId.substring(`${summaryID}-`.length)
    : sectionId;

  try {
    const translKey = sectionHashToLabelTranslKey(
      normalizedId as ApiReferenceMemberTypeCollectionHash,
    );
    return translKey ? translate(translKey) : null;
  } catch {
    // If sectionHashToLabelTranslKey doesn't recognize it, return null
    return null;
  }
};

/**
 * Truncates a string to a specified max length and appends ellipsis if needed
 */
const truncateName = (name: string, maxLength: number): string => {
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}...`;
};

const ReferenceStickyBreadcrumb: React.FC<ReferenceStickyBreadcrumbProps> = ({
  currentName,
  currentType,
  classReferenceParents,
  onTocClick,
}) => {
  const { classes, cx } = useReferenceStickyBreadcrumbStyles();
  const theme = useTheme();
  const { translate } = useTranslation();
  const { isMobile } = useResponsiveDesign();
  const { currentElementId, pause: pauseElementsShowInView } = useElementsShowInView();
  const { contentDirFilePath, isOpenSourced, machineTranslated } = useAppConfig();
  const { isFetched: isAuthLoaded } = useRobloxAuthentication();
  const { isReady: isAllowFeedbackCheckReady, isDocsFeedbackAllowed } = useShareFeedbackProvider();

  const [isEmployee, setIsEmployee] = useState(false);
  const [showTocIcon, setShowTocIcon] = useState(false);
  // Combined state: count tracks collapsed items, key forces re-measurement
  const [collapseState, setCollapseState] = useState({ count: 0, key: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLElement>(null);

  useEffect(() => {
    groupsClient.isEmployee().then(setIsEmployee);
  }, []);

  // Find the scrollable container element (CreatorHubLayout.PageContent)
  const getScrollableContainer = useCallback((): HTMLElement | null => {
    const main = document.getElementById(mainID);
    if (main) {
      let parent = main.parentElement;
      while (parent && parent !== document.body) {
        if (parent.scrollHeight > parent.clientHeight) {
          return parent;
        }
        parent = parent.parentElement;
      }
    }
    return null;
  }, []);

  /**
   * Track if the heading TOC icon has scrolled past the sticky breadcrumb.
   * When the heading TOC is no longer visible, we show the TOC icon in the sticky breadcrumb.
   */
  useEffect(() => {
    const scrollContainer = getScrollableContainer();
    const targetElement = scrollContainer || window;

    const checkPosition = () => {
      // Query for the button on each check to handle DOM changes
      const headingTocButton = document.getElementById('reference-heading-toc-button');
      if (!headingTocButton || !containerRef.current) return;

      const stickyBottom = containerRef.current.getBoundingClientRect().bottom;
      const buttonRect = headingTocButton.getBoundingClientRect();

      // Use button center for balanced trigger point (middle ground between top and bottom)
      const buttonCenter = buttonRect.top + buttonRect.height / 2;

      // Show TOC icon when heading TOC center passes behind sticky breadcrumb
      // Small buffer (2px) prevents flickering at the exact transition point
      setShowTocIcon(buttonCenter < stickyBottom - 2);
    };

    targetElement.addEventListener('scroll', checkPosition, { passive: true });
    checkPosition(); // Initial check

    return () => {
      targetElement.removeEventListener('scroll', checkPosition);
    };
  }, [getScrollableContainer]);

  const showOpenSourceEditButton =
    isOpenSourced || isEmployee || process.env.environment !== 'production';

  /**
   * Build breadcrumb items: Type > [Parents] > Current Page > [Current Member]
   * Example: Classes > Instance > BasePart > Anchored
   */
  const breadcrumbItems = useMemo(() => {
    const items: { name: string; url: string; type: ReferenceType; isMember?: boolean }[] = [];

    // Add type label (e.g., "Classes", "Datatypes")
    const typeLabel = translate(labelTranslKeyByReferenceType[currentType]) || currentType;
    items.push({
      name: typeLabel,
      url: `/reference/engine/${referenceTypePathMapping[currentType]}`,
      type: currentType,
    });

    // Add parent classes (furthest ancestor first)
    if (classReferenceParents?.length) {
      const reversedParents = [...classReferenceParents].reverse();
      reversedParents.forEach((parent) => {
        items.push({
          name: parent.name,
          url: getReferencePageUrl(ReferenceType.CLASS, parent.name),
          type: ReferenceType.CLASS,
        });
      });
    }

    // Add current page
    items.push({
      name: currentName,
      url: getReferencePageUrl(currentType, currentName),
      type: currentType,
    });

    // Add current member/section if viewing a specific element
    if (currentElementId) {
      const isInheritedSection = currentElementId.includes('-inherit-');
      const isSummary = currentElementId === summaryID;

      // Skip inherited sections and summary, but include other sections with proper names
      if (!isInheritedSection && !isSummary) {
        const sectionName = getSectionName(currentElementId, translate);
        const displayName = sectionName || currentElementId; // Use section name if available, otherwise use ID
        const isSection = !!sectionName;

        items.push({
          name: displayName,
          url: `${getReferencePageUrl(currentType, currentName)}#${currentElementId}`,
          type: currentType,
          isMember: !isSection, // Only non-section items are members
        });
      }
    }

    return items;
  }, [classReferenceParents, currentName, currentType, translate, currentElementId]);

  /**
   * Detect when breadcrumb items wrap to a new line.
   * Measures the breadcrumb's height - if it exceeds a single line, items have wrapped.
   */
  const detectWrapping = useCallback((): boolean => {
    const breadcrumb = breadcrumbRef.current;
    if (!breadcrumb) return false;

    // Get the computed line height or use a reasonable default
    const computedStyle = window.getComputedStyle(breadcrumb);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 24;

    // If breadcrumb height exceeds ~1.5x line height, items have wrapped to a new line
    const breadcrumbHeight = breadcrumb.getBoundingClientRect().height;
    return breadcrumbHeight > lineHeight * 1.5;
  }, []);

  /**
   * Iteratively increase collapse count until breadcrumbs fit on one line.
   * Each render checks if wrapping is detected and increments if needed.
   * Uses useLayoutEffect to measure before paint, minimizing visual flicker.
   */
  useLayoutEffect(() => {
    // Keep at least 3 items visible (1 before + 2 after the "...")
    const maxCollapsible = Math.max(0, breadcrumbItems.length - 3);
    const isWrapping = detectWrapping();

    // If wrapping is detected and we can collapse more, increment
    if (isWrapping && collapseState.count < maxCollapsible) {
      setCollapseState((s) => ({ ...s, count: s.count + 1 }));
    }
  }, [breadcrumbItems.length, collapseState, detectWrapping]);

  // Reset collapse when breadcrumb items change
  const prevItemsRef = useRef(breadcrumbItems);
  useEffect(() => {
    if (prevItemsRef.current !== breadcrumbItems) {
      prevItemsRef.current = breadcrumbItems;
      setCollapseState((s) => ({ count: 0, key: s.key + 1 }));
    }
  }, [breadcrumbItems]);

  // Reset and re-measure on container resize (debounced to avoid flickering during drag)
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout>;
    let lastWidth = 0;

    const resizeObserver = new ResizeObserver((entries) => {
      const newWidth = entries[0]?.contentRect.width || 0;

      // Only trigger on width changes, not height
      if (Math.abs(newWidth - lastWidth) < 1) return;
      lastWidth = newWidth;

      // Debounce: wait for resize to stop before measuring
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setCollapseState((s) => ({ count: 0, key: s.key + 1 }));
      }, 150);
    });

    // Observe the sticky container, not the breadcrumb itself
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, []);

  const handleBreadcrumbClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, isCurrentPage?: boolean, elementId?: string) => {
      if (!isCurrentPage && !elementId) {
        // Let default navigation happen for parent/type links
        return;
      }

      event.preventDefault();

      // Pause the intersection observer to prevent race conditions during scroll
      pauseElementsShowInView(200);

      if (isCurrentPage) {
        // Clicking the current page name scrolls to top and clears hash
        window.history.replaceState(null, '', window.location.pathname);
        const container = getScrollableContainer();
        (container || window).scrollTo({ top: 0, behavior: 'smooth' });
      } else if (elementId) {
        // Clicking a section/member scrolls and updates URL hash
        const newUrl = `${window.location.pathname}#${elementId}`;
        window.history.pushState(null, '', newUrl);

        const targetElement = document.getElementById(elementId);
        targetElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [getScrollableContainer, pauseElementsShowInView],
  );

  return (
    <div ref={containerRef} className={classes.container} data-testid='reference-sticky-breadcrumb'>
      <div className={classes.breadcrumbRow}>
        <div className={classes.breadcrumbContainer}>
          <Breadcrumbs
            ref={breadcrumbRef}
            separator='/'
            aria-label='API Reference breadcrumb'
            {...(collapseState.count > 0 && {
              maxItems: breadcrumbItems.length - collapseState.count,
              itemsAfterCollapse: 2,
              itemsBeforeCollapse: 1,
              slotProps: {
                collapsedIcon: {
                  sx: getCollapsedIconStyles(theme),
                },
              },
            })}>
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              const isMemberItem = item.isMember;
              const isCurrentPageItem = !isMemberItem && item.name === currentName;
              const displayName =
                isMobile && index < breadcrumbItems.length - 1
                  ? truncateName(item.name, 8)
                  : item.name;

              return (
                <DynamicallyRouted.Link
                  key={item.url}
                  href={item.url}
                  className={cx(classes.breadcrumbLink, {
                    [classes.currentPageLink]: isLast,
                  })}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                    handleBreadcrumbClick(
                      e,
                      isCurrentPageItem,
                      // Pass elementId for both members and sections (anything with currentElementId)
                      currentElementId || undefined,
                    )
                  }>
                  <Typography
                    className={cx(classes.breadcrumbText, {
                      [classes.parentItem]: !isLast,
                    })}
                    title={item.name}>
                    {displayName}
                  </Typography>
                </DynamicallyRouted.Link>
              );
            })}
          </Breadcrumbs>
        </div>

        {/* Right side: TOC icon or action buttons
                Mobile: Always shows TOC icon
                Desktop: Fades between action buttons and TOC icon based on scroll position */}
        <div className={classes.rightSideContainer}>
          {!isMobile && (
            <div
              className={cx(classes.actionButtonsContainer, {
                [classes.hidden]: showTocIcon,
              })}>
              <LanguageMenu engineRevampEnabled />
              <FeedbackButton
                pageTitle={currentName}
                show={!isAuthLoaded || !isAllowFeedbackCheckReady || isDocsFeedbackAllowed}
                engineRevampEnabled
              />
              {showOpenSourceEditButton && contentDirFilePath && (
                <OpenSourceEditButton
                  contentDirFilePath={contentDirFilePath}
                  isEmployee={isEmployee}
                  machineTranslated={machineTranslated}
                  engineRevampEnabled
                />
              )}
            </div>
          )}
          <div
            className={cx(classes.tocIconContainer, {
              [classes.hidden]: !isMobile && !showTocIcon,
            })}>
            <IconButton
              onClick={onTocClick}
              aria-label={translate('Label.TableOfContents') || 'Table of Contents'}
              size='small'
              className={classes.tocIcon}>
              <SvgIconTableOfContent />
            </IconButton>
          </div>
        </div>
      </div>
      <Divider className={classes.divider} />
    </div>
  );
};

export default ReferenceStickyBreadcrumb;
